// src/generators/dzial1_liczby_i_dzialania/temat1_dzialania/podtemat3_zbiory_types/InclusionExclusionModernTask.js
import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import FractionInput, { formatLatexPreview } from '../../../../components/inputs/FractionInput';

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const rand = (a, b) => a + Math.floor(Math.random() * (b - a + 1));

/* ======================================================================
   Losowanie przecięcia k = |A∩B| z szerokim, realistycznym rozkładem:
   - ~65% przypadków: 0 .. floor(0.5N)   (małe/średnie przecięcie)
   - ~15% przypadków: floor(0.5N)+1 .. N (duże przecięcie)
   - ~5% przypadków:  N                  (pełne pokrycie — wszyscy w obu)
   - ~15% przypadków: 25%..75% N         (środek – dla urozmaicenia)
   ====================================================================== */
function sampleKWide(N) {
  const r = Math.random();
  const half = Math.floor(0.5 * N);
  if (r < 0.05) {
    return N; // 5%: wszyscy w obu (maksymalne przecięcie)
  } else if (r < 0.20) {
    // 15%: duże przecięcie 50%..100% klasy
    return rand(Math.min(half + 1, N), N);
  } else if (r < 0.85) {
    // 65%: małe/średnie przecięcie 0..50% klasy
    return rand(0, half);
  } else {
    // 15%: środek rozkładu ~25%..75% N
    const q25 = Math.floor(0.25 * N);
    const q75 = Math.floor(0.75 * N);
    return rand(q25, Math.max(q25, q75));
  }
}

/* ===== generator danych — przecięcie =====
   Założenie treści: każdy spełnia ≥1 warunek ⇒ |A∪B| = N.
   Warunki: 12 ≤ A,B ≤ N oraz 0 ≤ k = |A∩B| ≤ min(A,B).
   Z relacji |A∩B| = A + B − N ⇒ B = N − A + k.
*/
function genNumbers() {
  const N = rand(26, 34);

  while (true) {
    const k = sampleKWide(N);

    // Zakres A tak, aby A ∈ [12, N] i B = N − A + k ∈ [12, N]
    const A_min = Math.max(12, k);
    const A_max = Math.min(N, N + k - 12);

    if (A_min > A_max) {
      // Ten k nie pozwala na żaden A spełniający B∈[12,N] – losujemy ponownie
      continue;
    }

    const A = rand(A_min, A_max);
    const B = N - A + k;

    if (A >= 12 && A <= N && B >= 12 && B <= N) {
      const inter = k;
      return { N, A, B, inter };
    }
  }
}

export function generateTask() {
  // Teraz tylko dwa scenariusze: gry i języki (social przeniesione do zadania o sumie)
  const scenario = pick(['games', 'languages']);
  const { N, A, B, inter } = genNumbers();

  let title, aLabel, bLabel, who, noun;
  if (scenario === 'games') {
    title = 'Gracze – dwie popularne gry';
    aLabel = 'gra w Minecrafta';
    bLabel = 'gra w Fortnite’a';
    who = 'uczniów';
    noun = 'grach';
  } else {
    title = 'Nauka języków';
    const langs = ['niemieckiego', 'francuskiego', 'hiszpańskiego'];
    const useEnglish = Math.random() < 0.7;
    const l1 = useEnglish ? 'angielskiego' : pick(langs);
    const l2 = useEnglish ? pick(langs) : pick(langs.filter((x) => x !== l1));
    aLabel = `uczy się ${l1}`;
    bLabel = `uczy się ${l2}`;
    who = 'osób';
    noun = 'językach';
  }

  return {
    type: 'InclusionExclusionModernTask',
    data: { scenario, title, N, A, B, inter, aLabel, bLabel, who, noun }
  };
}

export function renderTask({ data }) {
  const { title, N, A, B, aLabel, bLabel, who, scenario } = data;

  const unionSentence =
    scenario === 'games'
      ? 'Wiadomo, że każdy uczeń gra przynajmniej w jedną z tych gier (albo w Minecrafta, albo w Fortnite’a, albo w obie).'
      : 'Wiadomo, że każda osoba uczy się przynajmniej jednego z tych języków (albo pierwszego, albo drugiego, albo obu).';

  const question =
    scenario === 'games'
      ? 'Ilu uczniów gra w obie gry?'
      : 'Ile osób uczy się obu języków?';

  return (
    <div>
      <p><strong>{title}</strong></p>

      <p>
        W klasie jest <InlineMath math={String(N)} /> {who}. Z ankiety wynika, że:
        <br />– <InlineMath math={String(A)} /> {who} {aLabel},
        <br />– <InlineMath math={String(B)} /> {who} {bLabel}.
      </p>

      <p>{unionSentence} {question}</p>
    </div>
  );
}

/* === INPUT: spójny z resztą kursu === */
export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label=''
      placeholder='np. 14'
    />
  );
}

/* === CHECK: bez zmian === */
export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== 'object') return false;
  const given = userAnswer.parsed;
  if (given == null) return false;
  return Math.abs(Number(given) - Number(data.inter)) < 1e-9;
}

export function renderAnswer({ data }) {
  return <InlineMath math={String(data.inter)} />;
}

/* === WYJAŚNIENIE: bez zmian merytorycznych === */
export function renderExplanation({ data }) {
  const { N, A, B, inter } = data;
  const sum = A + B;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12, textAlign: 'left' }}>
      <p><strong>Krok 1:</strong> Korzystamy ze wzoru włączeń–wyłączeń:</p>
      <BlockMath math={'|A \\cap B| = |A| + |B| - |A \\cup B|'} />

      <p>
        <strong>Krok 2:</strong> W treści założono, że każdy spełnia przynajmniej jeden warunek,
        więc <InlineMath math={'|A \\cup B| = N'} />.
      </p>
      <BlockMath math={`|A \\cup B| = ${N}`} />

      <p>
        <strong>Krok 3 (intuicja):</strong>{' '}
        <InlineMath math={`|A| + |B| = ${A} + ${B} = ${sum}`} />{' '}
        {sum > N ? (
          <>
            jest większa niż <InlineMath math={`N = ${N}`} />, więc część osób policzyliśmy
            podwójnie — to właśnie <InlineMath math={'|A \\cap B|'} />.
          </>
        ) : (
          <>
            nie przekracza <InlineMath math={`N = ${N}`} />, więc przecięcie może być równe 0.
          </>
        )}
      </p>

      <p><strong>Krok 4:</strong> Podstawiamy i liczymy:</p>
      <BlockMath math={`|A \\cap B| = ${A} + ${B} - ${N} = ${inter}`} />

      <p><strong>Odpowiedź:</strong> <InlineMath math={String(inter)} />.</p>
    </div>
  );
}

/* === Podgląd odpowiedzi użytkownika: bez zmian === */
export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== 'object' || !('raw' in userAnswer)) {
    return <i>(brak odpowiedzi)</i>;
  }
  const latex = formatLatexPreview(userAnswer.raw);
  return <InlineMath math={latex || ''} />;
}
